import { signedFetch } from '../../super-picker/services/api';

const PREFIX = '/api/communication';

export interface SendWhatsAppRecipient {
  mobile: string;
  ceaNumber: string;
  name?: string;
  /** Ordered values for {{1}}, {{2}}, … placeholders. */
  variables?: string[];
}

export interface CreateWhatsAppTemplatePayload {
  message: string;
  purpose: string;
  category?: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  language?: string;
}

export interface WhatsAppTemplate {
  id: number;
  name: string;
  message: string;
  category: string;
  imageUrl: string | null;
  /**
   * True when the template carries an IMAGE-format HEADER at Meta. The send
   * endpoint requires `imageUrl` to be set when this is true; the edit form
   * uses this flag to prompt admins to upload a header image.
   */
  hasImg: boolean;
  /** BCP-47 language tag (e.g. `en_US`). Immutable after creation. */
  language: string;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateWhatsAppTemplateResponse extends WhatsAppTemplate {
  ok: true;
  templateId?: string;
}

export interface SendWhatsAppTemplateResponse {
  ok: true;
  id: number;
  status: string;
  recipientCount: number;
  broadcastScheduled: boolean;
}

export interface ImportTemplatesFromMetaResponse {
  ok: true;
  fetched: number;
  inserted: number;
  skipped: number;
  insertedNames: string[];
  skippedNames: string[];
}

/**
 * One-shot import: pulls every template registered against the configured
 * WhatsApp Business Account from Meta and inserts a local row for each one
 * that does not already exist (matched by name).
 */
export async function importWhatsAppTemplatesFromMeta(): Promise<ImportTemplatesFromMetaResponse> {
  // No body: signedFetch signs the empty string, which matches the backend's
  // canonical form for empty/JSON-`{}` bodies. Sending `JSON.stringify({})`
  // here would sign `"{}"` while the backend signs `""`, causing a signature
  // mismatch (401 Invalid signature → unauthorized redirect).
  const response = await signedFetch(`${PREFIX}/whatsapp/templates/import-from-meta`, {
    method: 'POST',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to import templates from Meta');
  }
  return data as ImportTemplatesFromMetaResponse;
}

export async function listWhatsAppTemplates(): Promise<WhatsAppTemplate[]> {
  const response = await signedFetch(`${PREFIX}/whatsapp/templates`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to list WhatsApp templates');
  }
  return Array.isArray(data?.templates) ? (data.templates as WhatsAppTemplate[]) : [];
}

export async function createWhatsAppTemplate(
  payload: CreateWhatsAppTemplatePayload,
): Promise<CreateWhatsAppTemplateResponse> {
  const response = await signedFetch(`${PREFIX}/whatsapp/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to create WhatsApp template');
  }
  return data as CreateWhatsAppTemplateResponse;
}

export interface UpdateWhatsAppTemplatePayload {
  message: string;
  category?: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  /** Optional new header image. When omitted the existing image is retained. */
  imageDataUrl?: string;
}

export async function updateWhatsAppTemplate(
  id: number,
  payload: UpdateWhatsAppTemplatePayload,
): Promise<WhatsAppTemplate> {
  const response = await signedFetch(`${PREFIX}/whatsapp/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to update WhatsApp template');
  }
  return data as WhatsAppTemplate;
}

export interface TemplateHistoryRecipient {
  sentId: number;
  ceaNumber: string;
  mobile: string | null;
  purpose: string;
  sentAt: string | null;
}

export interface TemplateHistoryBatch {
  uuid: string;
  sentAt: string | null;
  recipientCount: number;
  recipients: TemplateHistoryRecipient[];
}

export async function getWhatsAppTemplateHistory(
  id: number,
): Promise<TemplateHistoryBatch[]> {
  const response = await signedFetch(`${PREFIX}/whatsapp/templates/${id}/history`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to load template history');
  }
  return Array.isArray(data?.batches) ? (data.batches as TemplateHistoryBatch[]) : [];
}

export async function sendWhatsAppTemplate(
  id: number,
  payload: {
    purpose: string;
    recipients: SendWhatsAppRecipient[];
  },
): Promise<SendWhatsAppTemplateResponse> {
  const response = await signedFetch(`${PREFIX}/whatsapp/templates/${id}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to send WhatsApp template');
  }
  return data as SendWhatsAppTemplateResponse;
}
